import { User, CalendarDays } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

export default function ThreeMonthOutlook({ leaves, resources, holidays }) {
  const today = new Date();
  const months = generateMonthsData(today, leaves, holidays);
  const resourceDaysOff = calculateResourceDaysOff(resources, leaves, months);
  const activeResources = resources.filter(r => r.status === 'Active').slice(0, 15);

  return (
    <div className="space-y-6" data-testid="three-month-outlook">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        {months.map((month, idx) => (
          <MonthSummaryCard key={idx} month={month} />
        ))}
      </div>
      
      {/* Timeline Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Team Availability Timeline</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <div className="min-w-[800px]">
            <TimelineHeader months={months} />
            {activeResources.length > 0 ? (
              activeResources.map(resource => (
                <ResourceTimelineRow 
                  key={resource.id}
                  resource={resource}
                  months={months}
                  daysOff={resourceDaysOff[resource.id]}
                />
              ))
            ) : (
              <EmptyResourcesState />
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Holidays */}
      <HolidaysSection months={months} />
    </div>
  );
}

function generateMonthsData(today, leaves, holidays) {
  const months = [];
  for (let i = 0; i < 3; i++) {
    const monthDate = new Date(today.getFullYear(), today.getMonth() + i, 1);
    const monthName = monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    
    const monthLeaves = leaves.filter(leave => {
      if (leave.status !== 'approved') return false;
      const start = new Date(leave.start_date);
      const end = leave.end_date ? new Date(leave.end_date) : start;
      return start <= monthEnd && end >= monthStart;
    });
    
    const monthHolidays = holidays.filter(h => {
      const hDate = new Date(h.date);
      return hDate.getFullYear() === monthDate.getFullYear() && hDate.getMonth() === monthDate.getMonth();
    });
    
    months.push({ date: monthDate, name: monthName, leaves: monthLeaves, holidays: monthHolidays });
  }
  return months;
}

function calculateResourceDaysOff(resources, leaves, months) {
  const daysOff = {};
  resources.forEach(r => { daysOff[r.id] = { total: 0, byMonth: [0, 0, 0] }; });
  
  leaves.filter(l => l.status === 'approved').forEach(leave => {
    const rid = leave.resource_id;
    if (!daysOff[rid]) return;
    
    const start = new Date(leave.start_date);
    const end = leave.end_date ? new Date(leave.end_date) : start;
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    daysOff[rid].total += days;
  });
  
  return daysOff;
}

function MonthSummaryCard({ month }) {
  return (
    <Card className="border">
      <CardHeader className="pb-2">
        <CardTitle className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{month.name}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-light">{month.leaves.length}</span>
          <span className="text-sm text-muted-foreground">leaves</span>
        </div>
        {month.holidays.length > 0 && (
          <div className="text-xs text-amber-600 mt-1">+ {month.holidays.length} holidays</div>
        )}
      </CardContent>
    </Card>
  );
}

function TimelineHeader({ months }) {
  return (
    <div className="flex border-b">
      <div className="w-40 shrink-0 p-3 font-medium text-sm bg-muted/50">Team Member</div>
      {months.map((month, idx) => (
        <div key={idx} className="flex-1 p-3 text-center border-l">
          <span className="font-medium text-sm">{month.name}</span>
        </div>
      ))}
    </div>
  );
}

function ResourceTimelineRow({ resource, months, daysOff }) {
  const resourceDaysOff = daysOff || { total: 0 };
  return (
    <div className="flex border-b last:border-b-0 hover:bg-muted/30">
      <div className="w-40 shrink-0 p-3 flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
          {resource.display_name?.charAt(0) || 'U'}
        </div>
        <div className="truncate">
          <div className="text-sm font-medium truncate">{resource.display_name}</div>
          <div className="text-xs text-muted-foreground">{resourceDaysOff.total} days</div>
        </div>
      </div>
      {months.map((month, idx) => {
        const resourceLeaves = month.leaves.filter(l => l.resource_id === resource.id);
        return (
          <div key={idx} className="flex-1 p-2 border-l">
            {resourceLeaves.length > 0 ? (
              resourceLeaves.map(leave => <LeaveBlock key={leave.id} leave={leave} />)
            ) : (
              <div className="text-xs text-muted-foreground text-center py-2">Available</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function LeaveBlock({ leave }) {
  const startStr = new Date(leave.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return (
    <div 
      className="text-xs px-2 py-1 rounded mb-1"
      style={{ backgroundColor: `${leave.leave_type_color}20`, borderLeft: `3px solid ${leave.leave_type_color}` }}
    >
      <div className="font-medium">{leave.leave_type_name}</div>
      <div className="text-muted-foreground">{startStr}</div>
    </div>
  );
}

function EmptyResourcesState() {
  return (
    <div className="p-8 text-center text-muted-foreground">
      <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
      <p>No active team members found</p>
    </div>
  );
}

function HolidaysSection({ months }) {
  const allHolidays = months.flatMap(m => m.holidays);
  if (allHolidays.length === 0) return null;
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Upcoming Holidays</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2 md:grid-cols-3">
          {allHolidays.map(holiday => (
            <HolidayCard key={holiday.id} holiday={holiday} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function HolidayCard({ holiday }) {
  const dateStr = new Date(holiday.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
        <CalendarDays className="w-5 h-5 text-amber-600" />
      </div>
      <div>
        <div className="font-medium text-sm">{holiday.name}</div>
        <div className="text-xs text-muted-foreground">{dateStr}</div>
      </div>
    </div>
  );
}
